/**
 * Where a link in an outgoing message must point.
 *
 * Six call sites across two Workers repeated the same
 * `ENVIRONMENT === 'production' ? … : …` ternary — invitations, user/role mail,
 * the embedded portal and auth redirect, analysis notifications and the
 * transcribe worker. A link that lands in an inbox cannot be re-pointed after
 * the fact, so "which host do we send people to" must have exactly one answer,
 * not six copies of one that happen to agree today.
 *
 * The staging host is deliberately the Worker's own hostname
 * (`aica-ui-staging.grow2.ai`) and NOT `app.aelo.grow2.ai`: the latter is the
 * old PRODUCTION UI domain, alive only as a 301 to `app.aelo.cloud`. A staging
 * link pointing there walks the reader out of staging and into production.
 */

/** Production UI. Also the target of the retired `app.aelo.grow2.ai` redirect. */
export const PRODUCTION_APP_BASE_URL = 'https://app.aelo.cloud';

/** Staging UI, served by `aica-ui-worker-staging`. */
export const STAGING_APP_BASE_URL = 'https://aica-ui-staging.grow2.ai';

/**
 * Resolve the UI origin for a link we are about to send someone.
 *
 * Takes the raw `ENVIRONMENT` binding rather than a boolean so every caller
 * asks the question the same way; anything that is not exactly `'production'`
 * is treated as non-production, which fails toward the staging host rather
 * than mailing a staging user a production link.
 */
export function resolveAppBaseUrl(environment: string | undefined): string {
  return environment === 'production' ? PRODUCTION_APP_BASE_URL : STAGING_APP_BASE_URL;
}

/**
 * Query parameter that names the organization a link is about (#1232).
 *
 * The web app keeps ONE selected organization per browser (localStorage). A
 * person in organizations A and B who last looked at B and then opened A's
 * digest used to land on B's data under A's email — or on an upsell for a
 * tier only B lacks. The app reads this parameter on load, switches to the
 * organization only if the signed-in user is a member of it, and removes the
 * parameter from the address bar. It selects, it never grants: every API call
 * is still scoped by the server to the caller's own memberships.
 */
export const ORG_LINK_PARAM = 'org';

/**
 * Point an app link at the organization the message is about.
 *
 * Every link an email sends into the app goes through here — no template
 * hand-builds `?org=`. The existing path, query and fragment are kept; only
 * {@link ORG_LINK_PARAM} is set (replaced, if already present).
 *
 * `url` should be absolute — links that leave in an email are. An empty
 * `orgId`, or a `url` that does not parse (a relative path, a misconfigured
 * base), returns the link unchanged: a link that opens the last-selected
 * organization is the pre-#1232 behaviour, whereas a throw here would drop the
 * whole email — a digest, a pause notice — over one link.
 */
export function appLinkForOrg(url: string, orgId: string): string {
  if (!orgId) return url;
  try {
    const link = new URL(url);
    link.searchParams.set(ORG_LINK_PARAM, orgId);
    return link.toString();
  } catch {
    return url;
  }
}

/**
 * The deal card in the app (`/deals/<stateId>`, #766) for the organization the
 * message is about. One builder for every outgoing link to it — the CRM
 * comment and the CRM task — so the two can never point at different paths.
 * The card opens the deal's current state for any state id, so an old link
 * still opens the deal. No base address — no link (`null`).
 */
export function dealCardUrl(
  appBaseUrl: string | null | undefined,
  stateId: string,
  orgId: string
): string | null {
  if (!appBaseUrl) return null;
  return appLinkForOrg(
    `${appBaseUrl.replace(/\/+$/, '')}/deals/${encodeURIComponent(stateId.toLowerCase())}`,
    orgId
  );
}
